import {
  V2ListProductsForAccountApiArg,
  V2PagingMeta,
  V2Relationships,
  useV2ListProductsForAccountQuery,
} from "@generated";
import { Product } from "../types";
import { ProjectVariable } from "../types/ProjectVariables";

export interface HookArgs {
  page?: V2ListProductsForAccountApiArg["page"];
  filter?: V2ListProductsForAccountApiArg["filter"];
}

const useListProductsOnAccount = ({ page, filter }: HookArgs) => {
  const { data, error, isLoading, refetch } = useV2ListProductsForAccountQuery({
    include: ["account", "services"],
    page,
    filter,
  });

  let products: Product[] = [];
  let customError: string | null = null;
  let pageMeta = {
    recordCount: 0,
    pageCount: 0,
  };

  if (data?.data) {
    try {
      products = data.data.map((product) => {
        if (!product.id) {
          throw new Error("Missing product.id");
        }

        return {
          id: Number(product.id),
          type: product?.type || "",
          attributes: {
            name: product.attributes?.name || "",
            description: product.attributes?.description || "",
            sku: product.attributes?.sku || "",
            intProdID: product.attributes?.["product-id"] || "",
            mfgPartNum: product.attributes?.["manufacturer-part-number"] || "",
            units: product.attributes?.["unit-of-measure"] || "",
            category: product.attributes?.category || "",
            subcategory: product.attributes?.subcategory || "",
            unitPrice: product.attributes?.["unit-price"] || 0,
            unitCost: product.attributes?.["unit-cost"] || 0,
            listPrice: product.attributes?.["list-price"] || 0,
            variables:
              (product.attributes?.[
                "project-variables"
              ] as ProjectVariable[]) || [],
          },
        };
      });

      if (!data.meta) {
        throw new Error("Missing data.meta");
      }

      if (!data.meta["record-count"]) {
        throw new Error('Missing data.meta["record-count"]');
      }

      if (!data.meta["page-count"]) {
        throw new Error('Missing data.meta["page-count"]');
      }
      pageMeta = {
        recordCount: data.meta["record-count"],
        pageCount: data.meta["page-count"],
      };
    } catch (err) {
      if (err instanceof Error) {
        customError = err.message;
      } else {
        customError = "An unknown error occurred";
      }
    }
  }

  return {
    products,
    error: error || customError,
    isLoading,
    pageMeta,
    refetch,
  };
};

export default useListProductsOnAccount;
